// src/store/users/auth.ts
import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { apiCallBegan, ApiCallFailedPayload, isSuccessResponse } from "../api";
import { AppDispatch, AppState } from "../configureStore";
import SliceSingletonBase from "../types/SliceSingletonBase";
import User from "../../entities/User";
import i18n from "../../i18n";
import { IAuthProvider } from "./authProviders/IAuthProvider";
import { jwtDecode } from "jwt-decode";
import { msalInstance, initializeMsal, loginRequest } from "../../util/msal/config";

interface DecodedToken {
    role?: string | string[];
}

// ---------------- PROVIDER HANDLER ---------------- //
let authProvider: IAuthProvider;

// ---------------- SLICE ---------------- //
interface State extends SliceSingletonBase<User> {
    isLoggedIn: boolean;
    tokenLastFetched: number | null;
}

const initialState: State = {
    data: null,
    loading: false,
    lastFetch: null,
    error: null,
    isLoggedIn: false,
    tokenLastFetched: null
};

const slice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        loginRequested: auth => {
            auth.loading = true;
            auth.error = null;
        },
        userDataReceived: (auth, action: PayloadAction<User>) => {
            const now = Date.now();
            auth.data = action.payload;
            auth.loading = false;
            auth.isLoggedIn = true;
            auth.lastFetch = now;
            auth.tokenLastFetched = now;
        },
        loginRequestFailed: (auth, action: PayloadAction<ApiCallFailedPayload>) => {
            auth.loading = false;
            auth.error = action.payload;
        }
    }
});

export const { loginRequested, userDataReceived, loginRequestFailed } = slice.actions;
export default slice.reducer;

// ---------------- ACTIONS ---------------- //
export const logoutRequested = createAction("logoutRequested");
export const userLoggedOut = createAction("userLoggedOut");

// ---------------- ACTION CREATORS ---------------- //
export const loginUser =
    (UserId: string, Password: string) => async (dispatch: AppDispatch) => {
        const url = "/api/Authentication/SignIn";
        const response = await dispatch(
            apiCallBegan({
                url,
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    "Accept-Language": i18n.language
                },
                data: { UserId, Password },
                onStart: loginRequested.type,
                onSuccess: userDataReceived.type,
                onError: loginRequestFailed.type
            })
        );

        if (isSuccessResponse(response)) {
            localStorage.setItem("CurrentUser", JSON.stringify(response.data));
        }
        sessionStorage.removeItem("accessToken");
        return response;
    };

export const setAuthProvider = (provider: IAuthProvider) => {
    authProvider = provider;
};

export const loginWithProvider = () => async (dispatch: AppDispatch) => {
    // Don't set loading here - the redirect will reset state anyway
    try {
        await authProvider.login();
        // After redirect, page reloads and handleRedirect will be called
    } catch (error) {
        console.error("Login initiation failed:", error);
        dispatch(loginRequestFailed({
            code: 0,
            message: (error as Error).message,
            extraData: (error as Error).stack || String(error)
        }));
    }
};

export const logoutProvider = async (idToken?: string | null) => {
    if (authProvider) {
        await authProvider.logout(idToken);
    }
};

// Handle redirect after login
export const handleRedirect = () => (dispatch: AppDispatch) => {
    if (!authProvider) {
        return;
    }

    if (authProvider.handleRedirect) {
        authProvider.handleRedirect(dispatch, externalSignIn);
    }
};

export const refreshToken = () => async (dispatch: AppDispatch) => {
    if (!authProvider) return;

    try {
        const token = authProvider.refreshToken
            ? await authProvider.refreshToken()
            : authProvider.getToken();

        if (!token) throw new Error("No token available");

        const url = "/api/Authentication/RefreshToken";
        const response = await dispatch(
            apiCallBegan({
                url,
                method: "POST",
                headers: { ...authHeader(), "Accept-Language": i18n.language },
                onStart: loginRequested.type,
                onSuccess: userDataReceived.type,
                onError: loginRequestFailed.type
            })
        );

        if (isSuccessResponse(response)) {
            localStorage.setItem("CurrentUser", JSON.stringify(response.data));
        } else {
            await dispatch({ type: logoutRequested.type });
        }

        return response;
    } catch (error) {
        console.error("Token refresh failed:", error);
        dispatch(loginRequestFailed({
            code: 0,
            message: "Token refresh failed",
            extraData: error instanceof Error ? error.message : String(error)
        }));
    } finally {
        sessionStorage.removeItem("accessToken");
    }
};

export const externalSignIn =
    (token: string, useCache?: boolean) => async (dispatch: AppDispatch) => {
        const url = "/api/Authentication/ExternalSignIn";
        useCache = useCache ? useCache : false;
        const response = await dispatch(
            apiCallBegan({
                url,
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Accept-Language": i18n.language
                },
                data: JSON.stringify({ token, useCache }),
                onStart: loginRequested.type,
                onSuccess: userDataReceived.type,
                onError: loginRequestFailed.type
            })
        );

        if (isSuccessResponse(response)) {
            localStorage.setItem("CurrentUser", JSON.stringify(response.data));
        }
        sessionStorage.removeItem("accessToken");
        return response;
    };
export const replaceUser = (userData: User) => (dispatch: AppDispatch) => {
    localStorage.setItem("CurrentUser", JSON.stringify(userData));
    sessionStorage.removeItem("accessToken");
    dispatch({ type: userDataReceived.type, payload: userData });
};

// ---------------- SELECTORS ---------------- //
export const getWorkerId = (state: AppState) => state.users.auth.data?.WorkerId;

// ---------------- UTILITY ---------------- //
export const adlogin = async () => {
    try {
        await initializeMsal();
        msalInstance.loginRedirect(loginRequest);
    } catch (error) {
        console.error("Error during login:", error);
    }
};

export const authHeader = (): Record<string, string> => {
    try {
        const currentUser = localStorage.getItem("CurrentUser");
        if (!currentUser) {
            return { Authorization: `Bearer EMPTY` };//force logout if no user is found
        }

        const token = JSON.parse(currentUser).SecurityToken;
        return token ? { Authorization: `Bearer ${token}` } : { Authorization: `Bearer EMPTY` };
    } catch (e) {
        console.error("Error parsing CurrentUser from localStorage", e);
        return { Authorization: `Bearer EMPTY` };//force logout if parsing fails
    }
};

export const isAuthenticated = (): boolean => {
    const currentUser = localStorage.getItem("CurrentUser");
    if (!currentUser) return false;

    try {
        JSON.parse(currentUser);
        return true;
    } catch (e) {
        console.error("Error parsing CurrentUser from localStorage", e);
        return false;
    }
};

export const getUserRoles = (): string[] => {
    const currentUser = localStorage.getItem("CurrentUser");
    if (!currentUser) return [];

    try {
        const token = JSON.parse(currentUser).SecurityToken;
        if (!token) return [];

        const decodedToken = jwtDecode<DecodedToken>(token);
        if (typeof decodedToken.role === "string") return [decodedToken.role];
        if (Array.isArray(decodedToken.role)) return decodedToken.role;
        return [];
    } catch (e) {
        console.error("Error decoding token", e);
        return [];
    }
};